import React, { useState, useEffect, useCallback, useContext } from "react";
import "./transactions.css";
import { authContext } from "../../context/authContext";
import apiClient from "../../utils/apiClient";
import Loader from "../../components/Loader";

const PAGE_SIZE = 10;

const formatAmount = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const formatDate = (timestamp) => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusClass = (status) => {
  const normalized = status?.toLowerCase() || "";
  if (["posted", "succeeded", "paid"].includes(normalized)) return "status-pill--success";
  if (["pending", "processing", "open"].includes(normalized)) return "status-pill--pending";
  if (["failed", "canceled", "void"].includes(normalized)) return "status-pill--failed";
  return "status-pill--neutral";
};

const Transactions = () => {
  const { accountId } = useContext(authContext);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorStack, setCursorStack] = useState([null]);

  const fetchPage = useCallback(
    async (startingAfter, targetPageIndex) => {
      if (!accountId) {
        setLoading(true);
        setTransactions([]);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const body = { limit: PAGE_SIZE };
        if (startingAfter) {
          body.starting_after = startingAfter;
        }

        const { data } = await apiClient.post("list-transactions", body);

        if (data.error) {
          setError(data.error);
          setTransactions([]);
          setHasMore(false);
          return;
        }

        setTransactions(data.transaction || []);
        setHasMore(Boolean(data.hasMore));
        setPageIndex(targetPageIndex);
      } catch (err) {
        console.error(err);
        setError(
          err.response?.data?.error || err.message || "Could not load transactions."
        );
        setTransactions([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [accountId]
  );

  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
    fetchPage(null, 0);
  }, [accountId, fetchPage]);

  const goToNextPage = () => {
    if (!hasMore || loading || !transactions.length) return;

    const lastId = transactions[transactions.length - 1].id;
    const nextIndex = pageIndex + 1;
    const nextStack = [...cursorStack];
    if (nextStack.length <= nextIndex) {
      nextStack.push(lastId);
    }
    setCursorStack(nextStack);
    fetchPage(lastId, nextIndex);
  };

  const goToPreviousPage = () => {
    if (pageIndex <= 0 || loading) return;

    const prevIndex = pageIndex - 1;
    const cursor = cursorStack[prevIndex];
    fetchPage(cursor, prevIndex);
  };

  const refresh = () => {
    setCursorStack([null]);
    fetchPage(null, 0);
  };

  const pageNumber = pageIndex + 1;

  const isInitialLoading = loading && transactions.length === 0 && pageIndex === 0;

  if (isInitialLoading) {
    return (
      <div className="transactions-page transactions-page--loading" aria-busy="true">
        <Loader size={48} />
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <header className="transactions-header">
        <div>
          <p className="page-eyebrow">Activity</p>
          <h1 className="page-title">Transactions</h1>
        </div>
        <div className="transactions-header__actions">
          {!loading && transactions.length > 0 && (
            <span className="transactions-count">
              {transactions.length} on this page
            </span>
          )}
          <button
            type="button"
            className="transactions-refresh"
            onClick={refresh}
            disabled={loading || !accountId}
          >
            Refresh
          </button>
        </div>
      </header>

      <section className="transactions-card">
        {error && (
          <p className="transactions-error" role="alert">
            {error}
          </p>
        )}

        <div className="transactions-table-wrap">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Status</th>
                <th>Flow type</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="transactions-table__loading">
                    <Loader size={32} />
                  </td>
                </tr>
              ) : transactions.length ? (
                transactions.map((item) => (
                  <tr key={item.id}>
                    <td className="transactions-table__amount">
                      {formatAmount(item.amount)}
                    </td>
                    <td>
                      <span className={`status-pill ${statusClass(item.status)}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="transactions-table__flow">{item.flow_type}</td>
                    <td className="transactions-table__date">
                      {formatDate(item.created)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="transactions-table__empty">
                    <div className="empty-state">
                      <span className="empty-state__icon">↔</span>
                      <p className="empty-state__title">No transactions yet</p>
                      <p className="empty-state__text">
                        When you add money, withdraw, or send payments, they&apos;ll show up here.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(transactions.length > 0 || pageIndex > 0) && (
          <nav className="transactions-pagination" aria-label="Transaction pages">
            <button
              type="button"
              className="transactions-pagination__btn"
              onClick={goToPreviousPage}
              disabled={loading || pageIndex === 0}
            >
              Previous
            </button>
            <span className="transactions-pagination__info">Page {pageNumber}</span>
            <button
              type="button"
              className="transactions-pagination__btn"
              onClick={goToNextPage}
              disabled={loading || !hasMore}
            >
              Next
            </button>
          </nav>
        )}
      </section>
    </div>
  );
};

export default Transactions;
